<template>
<div class="product-item">
  <div class="product-item-inner" @click="toggleEdit(true)">
    <div class="image">
      <img :src="originalImage ? image : `https://${fqdn}/images/h/80/${image}`" v-if="image" @error="imageError" />
    </div>
    <div class="information">
      <span class="id" v-if="product.product_id">{{ product.product_id }}</span>
      <div class="title">
        <h3>{{ product.title }}</h3>
        <template v-if="addProduct && variants.length > 1">
          <div class="variant-tag">{{ variants.length }} Variants</div>
        </template>
      </div>
      <p>{{ product.description }}</p>
      <div class="price" :data-sale="!!product.sales_price">
        <span class="sale" v-if="product.sales_price">{{ product.sales_price_formatted }}</span>
        <span>{{ productPrice }}</span>
      </div>
    </div>
    <div class="actions" v-if="!noAdd">
      <template v-if="addProduct">
        <div class="add-product">
          <i :class="`${state === 'adding' ? 'mdi mdi-loading mdi-spin' : state === 'added' ?  'icon-checklist-black' : 'icon-plusCircle blue'}`" @click.stop.prevent="state === '' ? $emit('add') : null" />
        </div>
      </template>
      <template v-else>
        <div class="display-product" v-if="['live', 'liveStopping'].includes(broadcast.status.life_cycle_status)">
          <i @click.prevent.stop="display" :class="`${ displayState === 'toggling' ? 'mdi mdi-loading mdi-spin' : !timestamps.at(-1) || timestamps.at(-1).hide_at ? 'icon-eye-24 blue' : 'icon-stop red'}`" />
        </div>
        <div class="display-time" v-if="broadcast.status.life_cycle_status === 'complete' && videoTime.filter(vt => vt.variant_id === null).length > 0">
          <span v-for="time in videoTime" :key="time.timestamp" class="blue" @click.prevent.stop="$emit('gotoInVideo', time.time)">{{ time.timestamp }}</span>
        </div>
        <div class="move-product">
          <i :class="`icon-arrow-move-up ${index === 0 ? 'gray' : 'blue'}`" @click.prevent.stop="$emit('move', true)" />
          <i :class="`icon-arrow-move-down ${index === range ? 'gray' : 'blue'}`" @click.prevent.stop="$emit('move', false)" />
        </div>
        <div class="remove-product">
          <i :class="`${removing ? 'mdi mdi-loading mdi-spin' : 'icon-trash1 red'}`" @click.prevent.stop="removeProduct" />
        </div>
      </template>
    </div>

    <div class="open-variants" @click.prevent.stop="showVariants = !showVariants" v-if="variants && variants.length > 1 && !addProduct">
      <i :class="`icon-arrow-down-small-24 ${ showVariants ? 'rotate-180' : '' }`" /> {{ showVariants ? 'Collapse' : 'Expand' }} {{ variants.length }} variants
    </div>
  </div>

  <div class="variants" :data-show="showVariants" v-if="variants && variants.length > 1 && !addProduct">
    <ProductVariant
      v-for="(variant, index) in variants"
      :key="`variant-${index}`"
      :productID="product.id"
      :variant="variant"
      @gotoInVideo="$emit('gotoInVideo', $event)"
    />
  </div>

  <portal to="modals">
    <product-import :show="showEdit" :edit="editProduct" :isNotCustom="product.source !== 'custom'" @close="toggleEdit(false)" v-if="edit" />
  </portal>
</div>
</template>

<script>

import { toHoursAndMinutes } from '@/utils';
import swal from 'sweetalert';

import ProductVariant from '@/components/Products/ProductVariant.vue';
import ProductImport from '@/components/Products/ProductImport.vue';

export default {
  props: {
    product: { type: Object, required: true },
    index: { type: Number, default: 0 },
    range: { type: Number, default: 0 },
    noAdd: { type: Boolean, default: false },
    originalImage: { type: Boolean, default: false },
    addProduct: { type: Boolean, default: false },
    state: { type: String, default: '' },
  },
  components: {
    ProductVariant,
    ProductImport,
  },
  computed: {
    fqdn() {
      return this.longfqdn;
    },
    /**
     * Return the product image
     * @returns {String}
     */
    image() {
      return this.product.image_url || this.product.image;
    },
    /**
     * Return the current price
     * @returns {String}
     */
    productPrice() {
      const { product } = this;
      if (product.price_formatted) {
        return product.price_formatted.trim() === '0' ? 'No price' : product.price_formatted;
      }

      return product.price === 0 ? 'No price' : `${parseInt(product.price, 10) / 100} ${product.currency ? product.currency.toUpperCase() : product.currency_code ? product.currency_code.toUpperCase() : ''}`;
    },
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Return the original attributes
     * @returns {Array}
     */
    attributes() {
      const { attributes } = JSON.parse(JSON.stringify(this.product));
      if (this.product.attributes_map) {
        Object.keys(this.product.attributes_map).forEach((attr) => {
          const attrIndex = attributes.findIndex(a => a === attr);
          if (attrIndex >= 0) {
            attributes[attrIndex] = this.product.attributes_map[attr];
          }
        });
      }

      return attributes ? attributes.map(attr => attr.toLowerCase()) : [];
    },
    /**
     * Event data
     */
    eventData() {
      return this.$store.getters['Products/eventProducts'].find(product => product.id === this.product.id);
    },
    timestamps() {
      return this.eventData?.timestamps.filter(ts => !ts.variant_id) || [];
    },
    /**
     * Return grouped variants for product
     * @returns {Array}
     */
    variants() {
      const {
        attributes,
        product: {
          attributes: productAttributes,
          variants,
        },
      } = this;

      if (attributes.length === 0) {
        return [];
      }

      let checkIndex = 0;
      if (attributes.indexOf('color') >= 0) {
        checkIndex = attributes.indexOf('color');
      } else if (attributes.indexOf('size') >= 0) {
        checkIndex = attributes.indexOf('size');
      }

      if (checkIndex >= 0) {
        const vars = [];

        if (variants) {
          variants.forEach((variant) => {
            if (!vars.find(v => v.attributes[productAttributes[checkIndex]] === variant.attributes[productAttributes[checkIndex]])) {
              vars.push(variant);
            }
          });

          if (vars[0].image_url) {
            return vars.filter((value, index, self) => index === self.findIndex(t => t.image_url === value.image_url));
          }

          return vars.filter((value, index, self) => index === self.findIndex(t => t.title === value.title));
        }
      }

      return [];
    },
    /**
     * Return array of times with minut:second the product was shown
     * @returns {Array}
     */
    videoTime() {
      if (!this.eventData) {
        return [];
      }

      const {
        broadcast: { actual_start_time },
        timestamps,
      } = this;

      const times = [];
      const y = new Date(actual_start_time);

      for (let i = 0; i < timestamps.length; i += 1) {
        let time = '';
        const x = new Date(timestamps[i].display_at);
        const displayTime = toHoursAndMinutes(Math.abs(x.getTime() - y.getTime()) / 1000);
        time += `${displayTime.h ? `${displayTime.h < 10 ? '0' : ''}${displayTime.h}:` : ''}${displayTime.m ? `${displayTime.m < 10 ? '0' : ''}${displayTime.m}:` : ''}${displayTime.s < 10 ? '0' : ''}${displayTime.s}`;
        if (timestamps[i].hide_at) {
          const z = new Date(timestamps[i].hide_at);
          const hideTime = toHoursAndMinutes(Math.abs(z.getTime() - y.getTime()) / 1000);
          time += `-${hideTime.h ? `${hideTime.h < 10 ? '0' : ''}${hideTime.h}:` : ''}${hideTime.m ? `${hideTime.m < 10 ? '0' : ''}${hideTime.m}:` : ''}${hideTime.s < 10 ? '0' : ''}${hideTime.s}`;
        }
        times.push({
          timestamp: time,
          time: Math.abs(x.getTime() - y.getTime()) / 1000,
          variant_id: timestamps[i].variant_id || null,
        });
      }
      return times;
    },
  },
  methods: {
    imageError() {
      this.$emit('imageProblem', true);
    },
    async removeProduct() {
      const willRemove = await swal({
        title: 'Are you sure?', text: 'This will remove the product from the broadcast', icon: 'warning', buttons: true, dangerMode: true,
      });

      if (!willRemove) {
        return;
      }

      this.removing = true;
      const bool = await this.$store.dispatch('Products/removeProductFromBroadcast', { id: this.broadcast.id, arr: [this.product.id] });
      this.removing = false;

      if (!bool) {
        swal({ title: 'An error occured', text: 'Could not remove product', icon: 'error' });
        return;
      }

      this.$nextTick(() => {
        this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
        this.$store.dispatch('Products/listBroadcastProducts', this.broadcast.id);
      });
    },
    async display() {
      if (this.displayState === 'toggling') {
        return;
      }

      const socketState = this.$store.getters['Events/socketState'](this.broadcast.id);

      if (socketState !== 'connected') {
        swal({ title: 'Can\'t toggle product', text: 'Not connected to websocket. Please reload browser and try again.', icon: 'error' });
        return;
      }

      const current = this.timestamps.at(-1);
      const event = !current || current.hide_at ? 'product.show' : 'product.hide';

      this.displayState = 'toggling';

      await this.$store.dispatch('Products/toggleEventProduct', {
        id: this.broadcast.id,
        payload: {
          event,
          id: this.product.id,
        },
      });

      this.displayState = '';
    },
    toggleEdit(bool) {
      if (this.addProduct) {
        return;
      }

      this.showEdit = bool;
      this.editProduct = this.product;

      if (bool) {
        this.edit = bool;
      } else {
        setTimeout(() => {
          this.edit = bool;
          this.editProduct = null;
        }, 350);
      }
    },
  },
  data() {
    return {
      showVariants: false,
      displayState: '',
      removing: false,
      edit: false,
      showEdit: false,
      editProduct: null,
    };
  },
};
</script>

<style lang="scss" scoped>
.product-item {
  position: relative;

  .product-item-inner {
    background-color: #FFFFFF;
    border-radius: 5px;
    box-shadow: 0 2px 8px 0 rgba(52,64,76,0.1), 0 0 0 1px rgba(170,182,195,0.30);
    cursor: pointer;
    display: flex;
    width: 100%;
    position: relative;
    z-index: 5;

    .image {
      img {
        border-radius: 5px 0 0 5px;
        aspect-ratio: 1;
        object-fit: contain;
        height: 100%;
        min-width: 80px;
        width: 80px;
      }
    }

    .information {
      overflow: hidden;
      padding: 8px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      span.id {
        font-family: 'Fira Mono', monospace;
        font-size: 10px;
        line-height: 12px;
        color: #727479;
      }

      h3, p {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      h3 {
        font-weight: 500;
        font-size: 15px;
        line-height: 20px;
      }

      p {
        color: #727479;
        font-size: 12px;
      }

      .title {
        display: flex;
        align-items: center;
      }

      .variant-tag {
        border: 1px solid #a8a7a7;
        color: #a8a7a7;
        border-radius: 5px;
        font-weight: 600;
        font-size: 13px;
        line-height: 20px;
        padding: 0 4px;
        margin-left: 5px;
        white-space: nowrap;
      }

      .price {
        font-size: 14px;

        &[data-sale="true"] {
          span:not(.sale) {
            text-decoration: line-through;
          }

          .sale {
            color: red;
            display: inline-block;
            margin-right: 5px;
          }
        }
      }
    }

    .open-variants {
      background-color: white;
      border-radius: 15px;
      box-shadow: 0 2px 8px 0 rgba(52,64,76,0.1), 0 0 0 1px rgba(170,182,195,0.30);
      color: #0062FF;
      display: flex;
      align-items: center;
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translate(-50%, -45%);
      font-weight: 500;
      font-size: 14px;
      padding: 3px 10px 3px 5px;
      user-select: none;

      i {
        font-size: 20px;
        transition: transform .3s;
      }
    }
  }

  .actions {
    margin-left: auto;
    display: flex;

    .gray {
      color: var(--color-cta-gray);
      pointer-events: none;
    }

    .blue {
      color: var(--color-cta-blue);
    }

    .red {
      color: var(--color-cta-red);
    }

    & > * {
      border-left: 1px solid #EEF0F9;
    }

    .display-time {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0 8px;

      span {
        cursor: pointer;
        font-family: "Fira Mono", monospace;
        font-size: 10px;
        white-space: nowrap;
      }
    }

    .move-product {
      display: flex;
      flex-direction: column;
      & > * {
        align-items: center;
        justify-content: center;
        display: flex;
        flex: 1;
        padding: 0 15px;
        font-size: 20px;

        &:first-child {
          border-bottom: 1px solid #EEF0F9;
        }
      }
    }

    .remove-product, .display-product, .add-product {
      display: flex;
      & > * {
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        padding: 0 15px;
        font-size: 20px;
      }
    }
  }
}
</style>

<style lang="scss">
.product-item {
  .variants {
    align-items: center;
    display: flex;
    flex-direction: column;
    z-index: 4;
    gap: 0px;
    transition: all .3s;

    .variant {
      min-height: 0px;
      max-height: 0;

      &:nth-child(1) {
        box-shadow: 0 2px 8px 0 rgba(52,64,76,0.1), 0 0 0 1px rgba(170,182,195,0.30);
        max-width: 98%;
        margin-top: -5px;
        z-index: 3;
        min-height: 10px;
      }
      &:nth-child(2) {
        box-shadow: 0 2px 8px 0 rgba(52,64,76,0.1), 0 0 0 1px rgba(170,182,195,0.30);
        margin-top: -5px;
        z-index: 2;
        max-width: 96%;
        min-height: 10px;
      }

      &:not(:nth-child(1)) {
        max-width: 96%;
      }
    }

    &:not([data-show="true"]) {
      .variant {
        & > * {
          display: none;
        }
      }
    }

    &[data-show="true"] {
      margin-top: 4px;
      gap: 4px;
      .variant {
        box-shadow: 0 2px 8px 0 rgba(52,64,76,0.1), 0 0 0 1px rgba(170,182,195,0.30);
        max-width: 98%;
        max-height: 500px;
        margin: 0;
      }
    }
  }
}
</style>
